import React, { createContext, useCallback, useState, useEffect } from "react";
import { useAssignments } from "../../shared/hooks/use-assignments";
import { useDefaultFilters } from "../../shared/hooks/use-default-filters";
import { useFieldTransformer } from "../../shared/hooks/use-field-transformer";
import { useForm } from "react-hook-form";
import moment from "moment";
import { flatten } from "flat";
import { basicOptions } from "../../components/task-list/lib/constants";

export const AdvancedFiltersContext = createContext({});

export const AdvancedFiltersProvider = ({
    optionsAssignments,
    filtersModel,
    defaultFilter,
    locations,
    statusTypes,
    storedFilters,
    initialAssignmentParams,
    clearFilters,
    task,
    onClose,
    onFiltersSubmit,
    children,
}) => {
    const advancedFilterStoreKey = `${task}-advanced-filters`;
    const { userAssignmentOptions, assignmentOptions, updateTeamAssignments, updateUserAssignments, populateUserAssignments } = useAssignments(
        task,
        initialAssignmentParams
    );
    const getBasicDefaultFilters = useDefaultFilters();
    const [appliedFilters, setAppliedFilters] = useState(storedFilters); // this is local state for applied filters
    const { transform, remapFormDefaultValues } = useFieldTransformer(filtersModel);
    // TODO: currently this is going to be attached to every mapper, make it optional since not every filter will use these option mappers
    const optionsAssignmentsMap = {
        ...optionsAssignments,
        assignmentGroupKey: [...basicOptions, ...assignmentOptions],
        assignedUserInfo: [...basicOptions, ...userAssignmentOptions],
    };

    const handleClose = () => {
        updateFormFields(true, storedFilters);
        onClose();
    };

    const setDefaultValues = () => {
        return storedFilters.reduce((defaultFilters, filter) => {
            defaultFilters[filter.id] = filter.value;

            return defaultFilters;
        }, {});
    };

    const { control, handleSubmit, reset, getValues } = useForm({
        defaultValues: setDefaultValues(),
    });

    const narrowAssignmentDropdowns = (filters) => {
        const assignmentGroupFilter = filters.find(({ id }) => id === "assignmentGroupKey");
        const assignedUserInfoFilter = filters.find(({ id }) => id === "assignedUserInfo");

        if (assignmentGroupFilter) {
            updateTeamAssignments(assignmentGroupFilter.value, true);
        }

        if (assignedUserInfoFilter) {
            updateUserAssignments(assignedUserInfoFilter.value, true);
        }
    };

    const onSubmit = (form) => {
        onFiltersSubmit(transform(form, optionsAssignmentsMap));
    };

    const updateFormDefaultValues = (acc, filter) => {
        // use stored id's if they were already saved in local storage
        if (storedFilters.length) {
            acc[filter.id] = filter.value;
        } else {
            const field = storedFilters.length ? filter.id : filtersModel[filter.id];
            acc[field.name] = filter.value;
        }

        return acc;
    };

    const updateFormFields = (resetToDefault, formFilters) => {
        // if there is no default filter, apply filters without them
        if (!defaultFilter) {
            let existingFormValues = {};

            if (!resetToDefault) {
                existingFormValues = flatten(getValues(), { safe: true });
            }

            const formDefaultValues = remapFormDefaultValues(formFilters);
            reset({ ...existingFormValues, ...formDefaultValues });

            return;
        }

        const [state] = defaultFilter;

        const filters = getBasicDefaultFilters(
            state.value,
            task,
            initialAssignmentParams.users,
            advancedFilterStoreKey,
            initialAssignmentParams.availableAssignments,
            populateUserAssignments,
            formFilters
        );

        if (filters.length) {
            let existingFormValues = {};
            narrowAssignmentDropdowns(filters);

            if (!resetToDefault) {
                existingFormValues = flatten(getValues(), { safe: true });
            }

            const formDefaultValues = filters.reduce(updateFormDefaultValues, {});
            reset({ ...existingFormValues, ...formDefaultValues });
        }
    };

    const handleClearFilter = useCallback(
        (id) => {
            const newFilters = appliedFilters.map((filter) => {
                if (filter.id !== id) {
                    return filter;
                }

                return {
                    id,
                    value: "",
                };
            });

            setAppliedFilters(newFilters);
            updateFormFields(false, newFilters);
        },
        [
            updateFormFields,
            appliedFilters,
            reset,
            clearFilters,
            initialAssignmentParams,
            populateUserAssignments,
            getBasicDefaultFilters,
            defaultFilter,
            task,
        ]
    );

    const handleClearAllFilters = useCallback(() => {
        const newFilters = appliedFilters.map((filter) => ({ ...filter, value: "" }));
        setAppliedFilters(newFilters);
        updateFormFields(false, newFilters);
    }, [
        updateFormFields,
        appliedFilters,
        reset,
        clearFilters,
        initialAssignmentParams,
        populateUserAssignments,
        getBasicDefaultFilters,
        defaultFilter,
        task,
    ]);

    useEffect(() => {
        // on filters reset, set filters back to default state
        if (!clearFilters) {
            return;
        }

        updateFormFields(true);
    }, [reset, clearFilters, initialAssignmentParams, populateUserAssignments, getBasicDefaultFilters, defaultFilter, task]);

    useEffect(() => {
        if (!defaultFilter) {
            return;
        }

        // this should contain only 'state' filter - value is set by BE
        const [state] = defaultFilter;

        if (!state || storedFilters.length) {
            narrowAssignmentDropdowns(storedFilters);

            return;
        }

        updateFormFields(false);
    }, [initialAssignmentParams, defaultFilter, task, getBasicDefaultFilters, populateUserAssignments]);

    // update team/user assignment form values if they are empty
    useEffect(() => {
        const formValues = transform(getValues());

        if (!formValues) {
            return;
        }

        const assignmentGroupKey = formValues.find(({ id }) => "assignmentGroupKey" === id);
        const user = formValues.find(({ id }) => id === "assignedUserInfo");

        if (assignmentGroupKey) {
            const selectedAssignmentOptionExists = optionsAssignmentsMap["assignmentGroupKey"].find(
                ({ value }) => assignmentGroupKey.value === value.toString()
            );

            // it means form field has to be reset because new options does not contain this value anymore
            if (!selectedAssignmentOptionExists) {
                reset({ ...getValues(), assignmentGroupKey: "" });
            }
        }

        if (user) {
            // mapper may have a different value than form field name
            const selectedUserAssignmentOptionExists = optionsAssignmentsMap["assignedUserInfo"].find(({ value }) => user.value === value.toString());

            if (!selectedUserAssignmentOptionExists) {
                reset({
                    ...getValues(),
                    assignedUserInfo: "",
                });
            }
        }
    }, [assignmentOptions, userAssignmentOptions]);

    const findDefaultValues = useCallback((options, values) => {
        let defaultValues = [];

        if (options && Array.isArray(values)) {
            defaultValues = options.filter((option) => {
                return values.some((value) => value === option.value || value === option.text);
            });
        }

        return defaultValues;
    }, []);

    return (
        <AdvancedFiltersContext.Provider
            value={{
                control,
                appliedFilters,
                handleSubmit,
                onSubmit,
                handleClose,
                onClose,
                findDefaultValues,
                handleClearAllFilters,
                handleClearFilter,
                updateTeamAssignments,
                updateUserAssignments,
                userAssignmentOptions,
                assignmentOptions,
                locations,
                statusTypes,
                filtersModel,
                task,
            }}
        >
            {children}
        </AdvancedFiltersContext.Provider>
    );
};
